// The public site's small interactions. Every page works without this script: tabs fall back to
// the first panel, prices show yearly, the carousel shows its first story, help shows every guide.
import './site-demos.js';
import './popups.js';
import './theme-toggle.js';

const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

// Hero product shot: tilted back, it levels out and follows the pointer as you move over it.
document.querySelectorAll<HTMLElement>('[data-tilt]').forEach(stage => {
  if (reduced) return;
  const tilt = stage.querySelector<HTMLElement>('.stage-tilt')!;
  stage.addEventListener('pointermove', e => {
    const r = stage.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5, y = (e.clientY - r.top) / r.height - 0.5;
    tilt.style.setProperty('--rx', `${(4 - y * 6).toFixed(1)}deg`);
    tilt.style.setProperty('--ry', `${(x * 8).toFixed(1)}deg`);
  });
  stage.addEventListener('pointerleave', () => { tilt.style.removeProperty('--rx'); tilt.style.removeProperty('--ry'); });
});

// Pricing: monthly or yearly. Each price carries both in data attributes.
document.querySelectorAll<HTMLElement>('[data-billing]').forEach(group => {
  const buttons = [...group.querySelectorAll<HTMLButtonElement>('button[data-period]')];
  buttons.forEach(b => b.addEventListener('click', () => {
    const period = b.dataset.period as 'monthly' | 'yearly';
    buttons.forEach(x => x.setAttribute('aria-pressed', String(x === b)));
    document.querySelectorAll<HTMLElement>('[data-monthly][data-yearly]').forEach(el => {
      el.textContent = el.dataset[period]!;
      if (el.tagName === 'B') { el.classList.remove('flip'); void el.offsetWidth; el.classList.add('flip'); }
    });
  }));
});

// A choice that reloads the page with it (the pricing page's income band).
document.querySelectorAll<HTMLSelectElement>('select[data-autosubmit]').forEach(sel => sel.addEventListener('change', () => sel.form?.requestSubmit()));

// Feature tabs: click, arrow keys, and #giving style links from other pages. data-tabs="quiet"
// (the API documentation's examples) leaves the address alone.
document.querySelectorAll<HTMLElement>('[data-tabs]').forEach(root => {
  const quiet = root.dataset.tabs === 'quiet';
  const tabs = [...root.querySelectorAll<HTMLButtonElement>('[role="tab"]')];
  const show = (tab: HTMLButtonElement, focus = false) => {
    tabs.forEach(t => {
      const on = t === tab;
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
      document.getElementById(t.getAttribute('aria-controls')!)!.hidden = !on;
    });
    if (focus) tab.focus();
  };
  tabs.forEach((t, i) => {
    t.addEventListener('click', () => { show(t); if (!quiet) history.replaceState(null, '', `#${t.id.replace('tab-', '')}`); });
    t.addEventListener('keydown', e => {
      const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
      if (step) { e.preventDefault(); show(tabs[(i + step + tabs.length) % tabs.length]!, true); }
    });
  });
  if (quiet) return;
  const fromHash = () => { const t = tabs.find(x => x.id === `tab-${location.hash.slice(1)}`); if (t) show(t); };
  fromHash();
  addEventListener('hashchange', fromHash);
});

// Customer stories: a 3D ring that turns one story at a time.
document.querySelectorAll<HTMLElement>('[data-carousel]').forEach(root => {
  const ring = root.querySelector<HTMLElement>('.ring')!;
  const dots = [...root.querySelectorAll<HTMLButtonElement>('.dots button')];
  const stories = [...root.querySelectorAll<HTMLElement>('.story')];
  const count = stories.length, step = 360 / count;
  let turn = 0;
  const go = (n: number) => {
    turn = n;
    const at = ((n % count) + count) % count;
    ring.style.setProperty('--angle', `${-n * step}deg`);
    dots.forEach((d, i) => d.setAttribute('aria-current', String(i === at)));
    stories.forEach((s, i) => { s.inert = i !== at; });
  };
  root.querySelector('[data-prev]')!.addEventListener('click', () => go(turn - 1));
  root.querySelector('[data-next]')!.addEventListener('click', () => go(turn + 1));
  dots.forEach((d, i) => d.addEventListener('click', () => go(turn - (((turn % count) + count) % count) + i)));
  go(0);
});

// Why Adab: a week without, a week with.
document.querySelectorAll<HTMLElement>('[data-week]').forEach(root => {
  const buttons = [...root.querySelectorAll<HTMLButtonElement>('button[data-show]')];
  buttons.forEach(b => b.addEventListener('click', () => {
    buttons.forEach(x => x.setAttribute('aria-pressed', String(x === b)));
    root.querySelectorAll<HTMLElement>('[data-week-panel]').forEach(p => { p.hidden = p.dataset.weekPanel !== b.dataset.show; });
  }));
});

// Help: filter guides as you type; a match opens its answer.
const search = document.querySelector<HTMLInputElement>('[data-help-search]');
if (search) {
  const guides = [...document.querySelectorAll<HTMLDetailsElement>('[data-guide]')];
  const groups = [...document.querySelectorAll<HTMLElement>('[data-group]')];
  const none = document.querySelector<HTMLElement>('[data-no-hits]')!;
  const topics = document.querySelector<HTMLElement>('[data-topics]');
  const filter = () => {
    const q = search.value.trim().toLowerCase();
    guides.forEach(g => {
      const hit = !q || g.textContent!.toLowerCase().includes(q);
      g.hidden = !hit;
      g.open = Boolean(q) && hit;
    });
    groups.forEach(gr => { gr.hidden = !gr.querySelector('[data-guide]:not([hidden])'); });
    none.hidden = !q || guides.some(g => !g.hidden);
    if (topics) topics.hidden = Boolean(q);
  };
  // A link to a guide, or to a topic (like "Read the full guide"), opens what it points at.
  const openTarget = () => {
    const el = location.hash ? document.getElementById(decodeURIComponent(location.hash.slice(1))) : null;
    if (el instanceof HTMLDetailsElement) el.open = true;
    else el?.closest('[data-group]')?.querySelectorAll<HTMLDetailsElement>('[data-guide]').forEach(g => { g.open = true; });
  };
  openTarget();
  window.addEventListener('hashchange', openTarget);
  document.querySelectorAll<HTMLAnchorElement>('a[href^="#"]').forEach(a => a.addEventListener('click', () => setTimeout(openTarget)));
  search.addEventListener('input', filter);
  document.querySelectorAll<HTMLButtonElement>('[data-q]').forEach(b => b.addEventListener('click', () => { search.value = b.dataset.q!; filter(); search.focus(); }));
}

// Copy buttons on the API documentation's examples.
document.querySelectorAll<HTMLButtonElement>('[data-copy]').forEach(b => b.addEventListener('click', async () => {
  await navigator.clipboard.writeText(b.dataset.copy!);
  const was = b.textContent;
  b.textContent = 'Copied';
  setTimeout(() => { b.textContent = was; }, 1500);
}));

// Which links visitors click, for Platform > Visitors. A beacon, so leaving the page doesn't lose it.
document.addEventListener('click', e => {
  const a = (e.target as Element).closest?.<HTMLAnchorElement>('a[href]');
  if (!a) return;
  const label = (a.textContent || a.getAttribute('aria-label') || '').replace(/\s+/g, ' ').trim().slice(0, 120);
  navigator.sendBeacon('/v', new URLSearchParams({ path: location.pathname, target: a.getAttribute('href')!, label }));
});

// Cookie banner: save the visitor's choice for a year (read back server-side) and hide it.
document.querySelectorAll<HTMLButtonElement>('[data-consent]').forEach(b => b.addEventListener('click', () => {
  document.cookie = `cookie_consent=${b.dataset.consent}; path=/; max-age=31536000; samesite=lax`;
  b.closest<HTMLElement>('[data-cookie-banner]')!.remove();
}));
